import { baseApi } from 'src/utils'
import { setSession } from './utils'
import { useUserStore } from 'src/stores'
import { useResetStores } from 'src/hooks'
import { useMsal } from '@azure/msal-react'
import { encrypt } from 'src/utils/storage'
import { AuthContext } from './auth-context'
import { useEffect, useCallback, useMemo } from 'react'
import { API_ENDPOINTS } from 'src/services/api-endpoints'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import {
  REFRESH_TOKEN_KEY,
  STORAGE_KEY,
  AUTH_TYPE_KEY,
  AUTH_TYPE_MSAL,
  AUTH_TYPE_LOCAL,
} from 'src/constants'
import { useMsalTokenRefresh } from '../hooks/use-msal-token-refresh'

type Props = {
  children: React.ReactNode
}

export function AuthProvider({ children }: Props) {
  const { setUser } = useUserStore()
  const { instance, accounts } = useMsal()
  const { resetStores } = useResetStores()

  useMsalTokenRefresh()

  const acquireNewToken = useCallback(async () => {
    if (!accounts[0]) return null

    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['user.read'],
        account: accounts[0],
      })
      return response.idToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const popupResponse = await instance.acquireTokenPopup({
          scopes: ['user.read'],
        })
        return popupResponse.idToken
      }
      return null
    }
  }, [instance, accounts])

  const login = useCallback(
    async (MfaCode: string, Session: string, UserName: string) => {
      const response = await baseApi.post(API_ENDPOINTS.auth.login, {
        MfaCode,
        Session,
        UserName,
      })
      const accessToken = response.data.idToken

      localStorage.setItem(
        REFRESH_TOKEN_KEY,
        encrypt(response.data.refreshToken)
      )

      localStorage.setItem(AUTH_TYPE_KEY, AUTH_TYPE_LOCAL)

      localStorage.setItem(AUTH_TYPE_KEY, AUTH_TYPE_LOCAL)

      setSession(accessToken)

      const locale = localStorage.getItem('i18nextLng')

      const userResponse = await baseApi.get(API_ENDPOINTS.user.me, {
        params: { locale },
      })
      const user = userResponse.data

      setUser(user)
    },
    [setUser]
  )

  const loginWithMicrosoft = useCallback(async () => {
    const loginResponse = await instance.loginPopup({
      scopes: ['user.read'],
    })
    const accessToken = loginResponse.idToken

    localStorage.setItem(AUTH_TYPE_KEY, AUTH_TYPE_MSAL)

    setSession(accessToken)

    const locale = localStorage.getItem('i18nextLng')
    const userResponse = await baseApi.get(API_ENDPOINTS.user.me, {
      params: { locale },
    })
    const user = userResponse.data

    setUser(user)
  }, [instance, setUser])

  const logout = useCallback(async () => {
    const currentLanguage = localStorage.getItem('i18nextLng')
    const authType = localStorage.getItem(AUTH_TYPE_KEY)

    if (authType === AUTH_TYPE_MSAL) {
      localStorage.removeItem(AUTH_TYPE_KEY)
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.clear()
    }

    if (currentLanguage) {
      localStorage.setItem('i18nextLng', currentLanguage)
    }

    resetStores()
    setSession(null)
    window.location.href = '/'
  }, [resetStores])

  const initialize = useCallback(async () => {
    try {
      const authType = localStorage.getItem(AUTH_TYPE_KEY)
      const accessToken = localStorage.getItem(STORAGE_KEY)

      if (authType === AUTH_TYPE_MSAL) {
        if (accounts.length > 0) {
          try {
            const newToken = await acquireNewToken()
            if (newToken) {
              setSession(newToken)

              const locale = localStorage.getItem('i18nextLng')
              const { data } = await baseApi.get(API_ENDPOINTS.user.me, {
                params: { locale },
              })
              setUser(data)
            } else {
              await logout()
            }
          } catch (error) {
            await logout()
          }
        } else {
          await logout()
        }
      } else if (accessToken) {
        try {
          const locale = localStorage.getItem('i18nextLng')

          const { data } = await baseApi.get(API_ENDPOINTS.user.me, {
            params: { locale },
          })

          setUser(data)
        } catch (error: unknown) {
          const newToken = await acquireNewToken()

          if (newToken) {
            setSession(newToken)
          } else {
            await logout()
          }
        }
      }
    } catch (error) {
      logout()
    }
  }, [setUser, logout, acquireNewToken, accounts])

  useEffect(() => {
    initialize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const memoizedValue = useMemo(
    () => ({
      login,
      loginWithMicrosoft,
      logout,
      acquireNewToken,
    }),
    [login, loginWithMicrosoft, logout, acquireNewToken]
  )

  useEffect(() => {
    window.__AUTH_CONTEXT__ = memoizedValue
    return () => {
      window.__AUTH_CONTEXT__ = undefined
    }
  }, [memoizedValue])

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  )
}
